import React, { useState } from "react";
import { DownloadSimple, ShareNetwork, WhatsappLogo, Copy, Check } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * DesignActions — a compact "Download" + "Share" strip that customers can
 * use from either the DesignReview approval page or the public track page
 * to send their laser-cut design to family/friends.
 *
 * Share strategy (progressive enhancement):
 *   1. Try `navigator.share({files})` first — on mobile this opens the
 *      system share sheet (WhatsApp, Telegram, Gmail, Files app, etc.).
 *   2. Fall back to `navigator.share({url})` — copies the design link
 *      instead of the file (works on desktop-ish browsers).
 *   3. Final fallback: copy the design URL to clipboard + explicit
 *      WhatsApp deep-link button.
 *
 * Props:
 *   fileUrl        — full URL to the design file (image/PDF)
 *   fileName       — filename used when downloading / sharing
 *   orderNumber    — used in the share caption
 *   trackUrl       — public track URL to also copy for the recipient
 *   compact        — smaller pill layout (used above "Design Approved")
 */
export default function DesignActions({ fileUrl, fileName, orderNumber, trackUrl, compact = false }) {
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  const download = async () => {
    // Force a real download instead of navigating the tab. Fetch the file
    // into a Blob then create an <a download> — this works even when the
    // server returns `inline` disposition.
    setBusy(true);
    try {
      const resp = await fetch(fileUrl, { credentials: "omit" });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || `design-${orderNumber || "shobhit"}.bin`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Design download ho gaya");
    } catch (e) {
      // Last-resort fallback: open in a new tab
      window.open(fileUrl, "_blank", "noopener,noreferrer");
    } finally { setBusy(false); }
  };

  const shareCaption = () => {
    const parts = [
      `Mera Shobhit Creation ka design ready hai${orderNumber ? ` — Order #${orderNumber}` : ""}.`,
    ];
    if (trackUrl) parts.push(`Order track link: ${trackUrl}`);
    return parts.join("\n\n");
  };

  const share = async () => {
    setBusy(true);
    try {
      // Try native file share first (best UX on Android/iOS)
      if (navigator.canShare && navigator.share) {
        try {
          const resp = await fetch(fileUrl, { credentials: "omit" });
          const blob = await resp.blob();
          const file = new File([blob], fileName || `design-${orderNumber}.png`, {
            type: blob.type || "application/octet-stream",
          });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              files: [file],
              title: `Design — Order ${orderNumber}`,
              text: shareCaption(),
            });
            return;
          }
        } catch (_e) { /* fall through to URL share */ }

        // File share not supported → share caption + track URL
        try {
          await navigator.share({
            title: `Design — Order ${orderNumber}`,
            text: shareCaption(),
            url: trackUrl || fileUrl,
          });
          return;
        } catch (_e) { /* user cancelled or unsupported */ }
      }

      // Desktop fallback: copy the direct design URL
      await navigator.clipboard.writeText(fileUrl);
      setCopied(true);
      toast.success("Design link clipboard me copy ho gaya — kisi ko bhi paste karke bhej dijiye");
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      toast.error("Share nahi ho paya. Manually download karke bhej dijiye.");
    } finally { setBusy(false); }
  };

  const shareViaWhatsApp = () => {
    // WhatsApp deep-link — customer can pick a contact and forward the
    // design URL. Note: WhatsApp can't inline-attach an image from a URL,
    // so recipient sees a link (which is fine — it's the track page).
    const text = encodeURIComponent(shareCaption());
    const url = `https://wa.me/?text=${text}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (compact) {
    return (
      <div className="flex flex-wrap items-center gap-2" data-testid="design-actions-compact">
        <button
          type="button"
          onClick={download}
          disabled={busy}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-slate-700 hover:border-emerald-500 hover:bg-emerald-500/10 text-slate-200 text-xs font-mono-num tracking-widest transition-colors disabled:opacity-40"
          data-testid="design-download-compact"
        >
          <DownloadSimple size={14} weight="bold" /> DOWNLOAD
        </button>
        <button
          type="button"
          onClick={share}
          disabled={busy}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-slate-700 hover:border-orange-500 hover:bg-orange-500/10 text-slate-200 text-xs font-mono-num tracking-widest transition-colors disabled:opacity-40"
          data-testid="design-share-compact"
        >
          {copied ? <Check size={14} weight="bold" className="text-emerald-400" /> : <ShareNetwork size={14} weight="bold" />}
          SHARE
        </button>
        <button
          type="button"
          onClick={shareViaWhatsApp}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-slate-700 hover:border-emerald-500 hover:bg-emerald-500/10 text-slate-200 text-xs font-mono-num tracking-widest transition-colors"
          data-testid="design-whatsapp-compact"
          title="WhatsApp par bhej dein"
        >
          <WhatsappLogo size={14} weight="fill" className="text-emerald-400" /> WHATSAPP
        </button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="design-actions-full">
      <Button
        type="button"
        onClick={download}
        disabled={busy}
        className="h-12 rounded-sm bg-emerald-500 hover:bg-emerald-600 text-white font-heading tracking-widest disabled:opacity-40"
        data-testid="design-download-btn"
      >
        <DownloadSimple size={18} weight="bold" className="mr-2" />
        DOWNLOAD
      </Button>
      <Button
        type="button"
        onClick={share}
        disabled={busy}
        className="h-12 rounded-sm bg-orange-500 hover:bg-orange-600 text-white font-heading tracking-widest disabled:opacity-40"
        data-testid="design-share-btn"
      >
        {copied ? <Copy size={18} weight="bold" className="mr-2" /> : <ShareNetwork size={18} weight="bold" className="mr-2" />}
        {copied ? "LINK COPIED" : "SHARE"}
      </Button>
      <Button
        type="button"
        onClick={shareViaWhatsApp}
        className="h-12 rounded-sm bg-[#25D366] hover:bg-[#1ebe57] text-white font-heading tracking-widest"
        data-testid="design-whatsapp-btn"
      >
        <WhatsappLogo size={18} weight="fill" className="mr-2" />
        WHATSAPP
      </Button>
    </div>
  );
}
